import { Component, OnInit, OnDestroy} from '@angular/core';
import * as _ from 'lodash';
import * as Chart from 'chart.js';
import { SamplingService } from '../../../../../services/sampling.service';
import SamplingModel from '../../../../../services/sampling.model';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import PreferencesModel from 'src/app/services/preferences.model';
import { PreferencesService } from 'src/app/services/preferences.service';
import PersonsModel from '../../../../../services/persons.model';
import {PersonsService} from '../../../../../services/persons.service';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component GrowthComponent
 * @selector app-growth
 * @description
 * The `GrowthComponent` is responsible for displaying growth-related metrics within the Farm Metrics dashboard.
 * It visualizes three key datasets:
 * 1. **Average Body Weight (ABW)**: Distribution of ponds based on the average weight of the stock.
 * 2. **Count**: Distribution of ponds based on the estimated stock count.
 * 3. **Average Daily Growth (ADG)**: Distribution of ponds based on the daily growth rate.
 *
 * ## Key Responsibilities:
 * - **Data Aggregation**: Fetches sampling data and aggregates it for ABW, Count, and ADG summaries.
 * - **Visualization**: Renders interactive charts (Doughnut or Bar) using Chart.js for each metric.
 * - **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (Farmer, Farm Manager, Provider, etc.).
 * - **User Preferences**: Persists user's preferred chart type (Bar vs Doughnut) for each metric category.
 * - **Detailed Reporting**: Displays tabular lists of sampling details alongside the charts.
 *
 * ## User Roles & Permissions:
 * - **Farmer / Business Admin (Farm)**: View growth metrics for their specific farm business.
 * - **Farm Manager**: View metrics for farms assigned to them.
 * - **Business Admin (Provider)**: View metrics for farms associated with the provider.
 * - **Provider Manager**: View metrics for farms assigned to their technicians.
 * - **Employee**: View metrics for all farm sites (internal admin).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessName`: Business name.
 * - `loginBusinessCategory`: Business category.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `abwType`: Saved preference for ABW chart type.
 * - `countType`: Saved preference for Count chart type.
 * - `adgType`: Saved preference for ADG chart type.
 *
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Identifies user context from `localStorage`.
 *    - Fetches relevant farm sites based on role.
 *    - Calls `getOnLoadCallsData` with farm site IDs.
 * 2. **Data Fetching (`getOnLoadCallsData`)**:
 *    - Fetches ABW, Count, and ADG summaries and details concurrently.
 *    - Prepares chart data (`piekeys`, `piedata`, etc.).
 *    - Renders charts (`prepareABWChart`, `prepareCountChart`, `prepareADGChart`).
 * 3. **Interaction**:
 *    - User toggles chart types via selection methods (e.g., `abwgraphselection`).
 *    - User searches lists via search methods (e.g., `searchBusinessABW`).
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-growth',
  templateUrl: './growth.component.html',
  styleUrls: ['./growth.component.scss']
})
export class GrowthComponent implements OnInit, OnDestroy{
  /** @type {Array<FarmSiteModel>} List of farm sites associated with the user. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<PreferencesModel>} User preferences for chart display settings. */
  public preferences: Array<PreferencesModel>;

  /** @type {Array<SamplingModel>} List of sampling data (summary). */
  public samplings: Array<SamplingModel>;

  /** @type {Array<SamplingModel>} List of sampling data grouped by ABW location. */
  public abwLocations: Array<SamplingModel>;

  /** @type {Array<SamplingModel>} List of sampling data grouped by Count location. */
  public pondsByCountLocations: Array<SamplingModel>;

  /** @type {Array<SamplingModel>} List of sampling data grouped by ADG location. */
  public adgLocations: Array<SamplingModel>;

  /** @type {Array<SamplingModel>} Detailed list of ABW sampling data. */
  public abwDetailsList: Array<SamplingModel>;

  /** @type {Array<SamplingModel>} Detailed list of ADG sampling data. */
  public adgDetailsList: Array<SamplingModel>;

  /** @type {Array<SamplingModel>} Detailed list of Count sampling data. */
  public countDetailsList: Array<SamplingModel>;

  /** @type {Array<PersonsModel>} List of technicians (for Provider Manager role). */
  public technicians: Array<PersonsModel>;

  /** @type {Array<any>} List of technician IDs. */
  public technicianIds = new Array();

  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if the page data has loaded.
   */
  public pgLoad = false;

  /** @type {any} Keys (labels) for the ABW chart. */
  public piekeys: any;

  /** @type {any} Data values for the ABW chart. */
  public piedata: any;

  /** @type {any} Keys (labels) for the Count chart. */
  public inActivepiekeys: any;

  /** @type {any} Data values for the Count chart. */
  public inActivepiedata: any;

  /** @type {any} Keys (labels) for the ADG chart. */
  public pondSizepiekeys: any;

  /** @type {any} Data values for the ADG chart. */
  public pondSizepiedata: any;

  /** @type {string} Title for the ABW chart. */
  public charttitle: string;

  /** @type {string} Title for the Count chart. */
  public inActivecharttitle: string;

  /** @type {string} Title for the ADG chart. */
  public pondSizecharttitle: string;

  /** @type {Array<any>} List of farm site IDs used for data fetching. */
  public farmSiteIds = new Array();

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {string} Current chart type for ABW (e.g., 'bar', 'doughnut'). */
  public abwType: string;

  /** @type {any} Configuration object for the ABW chart. */
  public abwgraphconfig: any;

  /** @type {any} Chart.js instance for the ABW chart. */
  public abwgraph: any;

  /** @type {string} Current chart type for Count (e.g., 'bar', 'doughnut'). */
  public countType: string;

  /** @type {any} Configuration object for the Count chart. */
  public countgraphconfig: any;

  /** @type {any} Chart.js instance for the Count chart. */
  public countgraph: any;

  /** @type {string} Current chart type for ADG (e.g., 'bar', 'doughnut'). */
  public adgType: string;

  /** @type {any} Configuration object for the ADG chart. */
  public adggraphconfig: any;

  /** @type {any} Chart.js instance for the ADG chart. */
  public adggraph: any;

  /** @type {string} Default chart type if preference not found. */
  public chartType: string;

  /** @type {string} Search keyword (general - unused). */
  public search: string;

  /** @type {boolean} Flag indicating if the initial response has loaded. */
  public responseLoad: boolean;

  /** @type {string} Search keyword for ABW list. */
  public abwsearch: string;

  /** @type {string} Search keyword for Count list. */
  public countsearch: string;

  /** @type {string} Search keyword for ADG list. */
  public adgsearch: string;

  /**
   * @constructor
   * @description Initializes the component and services.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {PersonsService} personService - Service for person-related operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {PreferencesService} preferencesService - Service for managing user preferences.
   * @param {SamplingService} samplingService - Service for fetching sampling data.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous routes.
   */
  constructor(public sidenavaction: Sidenavaction, public personService: PersonsService, public farmSiteService: FarmSiteService, public preferencesService: PreferencesService, public samplingService: SamplingService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) { }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description
   * Lifecycle hook that initializes the component.
   * 1. Sets the previous route.
   * 2. Retrieves user context from localStorage.
   * 3. Determines the chart type preferences for ABW, Count, and ADG.
   * 4. Fetches farm sites based on the user's role and business category.
   * 5. Calls `getOnLoadCallsData` to fetch metrics data.
   *
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.farmSiteIds = new Array();
      if (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin') {
        this.abwType = localStorage.getItem('abwType');
        this.countType = localStorage.getItem('countType');
        this.adgType = localStorage.getItem('adgType');
        if (this.abwType == '') {
          this.chartType = 'bar';
        }
        if (this.countType == '') {
          this.chartType = 'bar';
        }
        if (this.adgType == '') {
          this.chartType = 'bar';
        }
      }
      // get person list based on login businessid
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByFarmsId(localStorage.businessID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByBAdminId(localStorage.businessID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else if (this.loginPersonType == 'Provider Manager') {
        this.technicians = await this.personService.getAllTechniciansByProviderManagerId(localStorage.personID);
        this.technicianIds = this.technicians.map(technician => technician.id);
        this.farmSites = await this.farmSiteService.getFarmeSitesByTechnicianIDs(this.technicianIds);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else {
        this.farmSites = await this.farmSiteService.getAllFarmSites();
        if (this.farmSites !== undefined) {
          this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
          this.getOnLoadCallsData(this.farmSiteIds);
        }
      }
      this.responseLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description
   * Lifecycle hook that cleans up resources when the component is destroyed.
   * Cancels ongoing HTTP requests for FarmSite, Sampling, and Preferences services.
   *
   * @returns {void}
   */
public ngOnDestroy(): void {
  this.farmSiteService.cancelOnGoingHttpRequests();
  this.samplingService.cancelOnGoingHttpRequests();
  this.preferencesService.cancelOnGoingHttpRequests();
}

  /**
   * @method getChattypeinDB
   * @async
   * @description
   * Fetches user preferences for chart types from the database.
   * Updates the chart type variables and re-renders charts if preferences exist.
   * @returns {Promise<void>}
   */
  public async getChattypeinDB() {
    try {
      let preferences = await this.preferencesService.getPeferencesbyUserIDBusinessID(localStorage.businessID, localStorage.userID);
      if (preferences.length > 0) {
        this.chartType = preferences[0].chartType;
      } else {
        this.chartType = 'doughnut';
      }
      this.prepareABWChart(this.chartType);
      this.prepareCountChart(this.chartType);
      this.prepareADGChart(this.chartType);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getOnLoadCallsData
   * @async
   * @description
   * Fetches sampling metrics data for the provided farm sites.
   * 1. Calls `getABWSummary`, `getCountSummary`, `getADGSummary`, `getABWDetails`, `getADGDetails`, and `getCountDetails` concurrently.
   * 2. Processes summary data for ABW, Count, and ADG charts.
   * 3. Loads user preferences for chart type if applicable.
   * 4. Renders the charts.
   *
   * @param {Array<any>} farmSiteIds - List of farm site IDs to fetch data for.
   * @returns {Promise<void>}
   */
  public async getOnLoadCallsData(farmSiteIds) {
    try {
      let promiseResults = new Array();
      await Promise.all([

        await this.samplingService.getABWSummary(farmSiteIds),
        await this.samplingService.getCountSummary(farmSiteIds),
        await this.samplingService.getADGSummary(farmSiteIds),
        await this.samplingService.getABWDetails(farmSiteIds),
        await this.samplingService.getADGDetails(farmSiteIds),
        await this.samplingService.getCountDetails(farmSiteIds)
      ]).then((resolve) => {
        promiseResults = resolve;
      }).catch((reject) => {
        alert(reject);
      });
      this.samplings = promiseResults[0];
      if (this.samplings !== undefined) {
        let chartdataobj = Object.assign({}, this.samplings);
        delete chartdataobj['pondsCount'];
        this.piekeys = Object.keys(chartdataobj);
        this.piedata = Object.values(chartdataobj);
        this.pgLoad = true;
        if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
          this.getChattypeinDB();
        } else {
          let abwcharttype = this.abwType || 'doughnut';
          this.prepareABWChart(abwcharttype);
        }
      }
      this.samplings = promiseResults[1];
      if (this.samplings !== undefined) {
        let chartdataobj1 = Object.assign({}, this.samplings);
        delete chartdataobj1['pondsCount'];
        this.inActivepiekeys = Object.keys(chartdataobj1);
        this.inActivepiedata = Object.values(chartdataobj1);
        this.pgLoad = true;
        if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
          this.getChattypeinDB();
        } else {
          let countcharttype = this.countType || 'doughnut';
          this.prepareCountChart(countcharttype);
        }
      }
      this.samplings = promiseResults[2];
      if (this.samplings !== undefined) {
        let chartdataobj2 = Object.assign({}, this.samplings);
        delete chartdataobj2['pondsCount'];
        this.pondSizepiekeys = Object.keys(chartdataobj2);
        this.pondSizepiedata = Object.values(chartdataobj2);
        this.pgLoad = true;
        if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
          this.getChattypeinDB();
        } else {
          let adgcharttype = this.adgType || 'doughnut';
          this.prepareADGChart(adgcharttype);
        }
      }
      this.abwDetailsList = promiseResults[3];
      this.adgDetailsList = promiseResults[4];
      this.countDetailsList = promiseResults[5];
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareABWChart
   * @description
   * Configures and renders the ABW chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `abwtype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} abwtype - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.prepareABWChart('doughnut');
   * @returns {void}
   */
  public prepareABWChart(abwtype: string) {
    this.charttitle = 'Ponds by ABW' + ' ' + ' ' + ' ';
    if (this.samplings['pondsCount']) {
      this.charttitle += this.samplings['pondsCount'] + ' ponds';
    }
    let ctx: any = document.getElementById('abwmyChart');
    let ctx1: any = ctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.piedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let abwnoData = false;
    if (isallareZeros.length == 0) {
      abwnoData = true;
    }
    // FOR NO DATA FOUND
    let legenddisply = true;
    if (abwtype == 'bar') {
      legenddisply = false;
    }
    this.abwgraphconfig = {
      // type: 'doughnut',

      data: {
        labels: this.piekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#FAF307',
            '#FAFF75',
            '#FE9A2E',
            '#DF7401',
            '#6AD276'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.piedata
        }]
      },
      options: {
        legend: {
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: abwnoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.abwgraphconfig);
    temp.type = abwtype || 'doughnut';
    // FOR NO DATA FOUND
    this.abwgraph = new Chart(ctx1, temp);
  }

  /**
   * @method abwgraphselection
   * @description
   * Handles the user's selection of chart type for ABW (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} abwtype - The selected chart type.
   * @example
   * this.abwgraphselection('bar');
   * @returns {void}
   */
  public abwgraphselection(abwtype: string) {
    // if (this.doughnutchecked == 'doughnut') {
    if (abwtype == 'bar') {
      let ele = '<canvas id=\'abwmyChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('abwmyChart');
      let ctx1: any = ctx.getContext('2d');

      if (this.abwgraph) {
        this.abwgraph.destroy();
      }
      this.abwgraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.abwgraphconfig);
      temp.type = 'bar';
      this.abwgraph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('abwType', temp.type);
      }
    } else if (abwtype == 'doughnut') {
      let ele = '<canvas id=\'abwmyChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('abwmyChart');
      let ctx1: any = ctx.getContext('2d');

      if (this.abwgraph) {
        this.abwgraph.destroy();
      }
      this.abwgraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.abwgraphconfig);
      temp.type = 'doughnut';
      this.abwgraph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('abwType', temp.type);
      }
    }
  }

  /**
   * @method getAllAbwLocation
   * @async
   * @description
   * Fetches ABW location summary data.
   * Populates `abwLocations`.
   * @returns {Promise<void>}
   */
  public async getAllAbwLocation() {
    try {
      this.abwLocations = await this.samplingService.getABWLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllCountsLocation
   * @async
   * @description
   * Fetches Count location summary data.
   * Populates `pondsByCountLocations`.
   * @returns {Promise<void>}
   */
  public async getAllCountsLocation() {
    try {
      this.pondsByCountLocations = await this.samplingService.getCountLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllAdgPondLocation
   * @async
   * @description
   * Fetches ADG location summary data.
   * Populates `adgLocations`.
   * @returns {Promise<void>}
   */
  public async getAllAdgPondLocation() {
    try {
      this.adgLocations = await this.samplingService.getADGLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareCountChart
   * @description
   * Configures and renders the Count chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `counttype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} counttype - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.prepareCountChart('bar');
   * @returns {void}
   */
  public prepareCountChart(counttype: string) {
    this.inActivecharttitle = 'Ponds by count' + ' ' + ' ' + ' ';
    if (this.samplings['pondsCount']) {
      this.inActivecharttitle += this.samplings['pondsCount'] + ' ponds';
    }
    let inActivectx: any = document.getElementById('countmyChart');
    let inActivectx1: any = inActivectx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.inActivepiedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let countnoData = false;
    if (isallareZeros.length == 0) {
      countnoData = true;
    }
    let legenddisply = true;
    if (counttype == 'bar') {
      legenddisply = false;
    }
    // FOR NO DATA FOUND
    this.countgraphconfig = {
      // type: 'doughnut',

      data: {
        labels: this.inActivepiekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#6AD276',
            '#FAFF75',
            '#FAF307',
            '#FE9A2E',
            '#DF7401',
            '#F84C06'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.inActivepiedata
        }]
      },
      options: {
        legend: {
          // display: countnoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: countnoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.countgraphconfig);
    temp.type = counttype || 'doughnut';
    // FOR NO DATA FOUND
    this.countgraph = new Chart(inActivectx1, temp);
  }

  /**
   * @method countgraphselection
   * @description
   * Handles the user's selection of chart type for Count (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} counttype - The selected chart type.
   * @example
   * this.countgraphselection('doughnut');
   * @returns {void}
   */
  public countgraphselection(counttype: string) {
    if (counttype == 'bar') {
      let ele = '<canvas id=\'countmyChart\'></canvas>';
      $('#countcanvasarea').html(ele);
      let inActivectx: any = document.getElementById('countmyChart');
      let inActivectx1: any = inActivectx.getContext('2d');

      if (this.countgraph) {
        this.countgraph.destroy();
      }
      this.countgraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.countgraphconfig);
      temp.type = 'bar';
      this.countgraph = new Chart(inActivectx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('countType', temp.type);
      }
    } else if (counttype == 'doughnut') {
      let ele = '<canvas id=\'countmyChart\'></canvas>';
      $('#countcanvasarea').html(ele);
      let inActivectx: any = document.getElementById('countmyChart');
      let inActivectx1: any = inActivectx.getContext('2d');

      if (this.countgraph) {
        this.countgraph.destroy();
      }
      this.countgraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.countgraphconfig);
      temp.type = 'doughnut';
      this.countgraph = new Chart(inActivectx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('countType', temp.type);
      }
    }
  }

  /**
   * @method prepareADGChart
   * @description
   * Configures and renders the ADG chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `adgtype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} adgtype - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.prepareADGChart('doughnut');
   * @returns {void}
   */
  public prepareADGChart(adgtype: string) {
    this.pondSizecharttitle = 'Ponds by ADG' + ' ' + ' ' + ' ';
    if (this.samplings['pondsCount']) {
      this.pondSizecharttitle += this.samplings['pondsCount'] + ' ponds';
    }
    let pondSizectx: any = document.getElementById('adgmyChart');
    let pondSizectx1: any = pondSizectx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.pondSizepiedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let adgnoData = false;
    if (isallareZeros.length == 0) {
      adgnoData = true;
    }

    let legenddisply = true;
    if (adgtype == 'bar') {
      legenddisply = false;
    }
    // FOR NO DATA FOUND
    this.adggraphconfig = {
      // type: 'doughnut',

      data: {
        labels: this.pondSizepiekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#FE9A2E',
            '#6AD276',
            '#F84C06',
            '#FAFF75',
            '#DF7401',
            '#FAF307'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.pondSizepiedata
        }]
      },
      options: {
        legend: {
          // display: adgnoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: adgnoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.adggraphconfig);
    temp.type = adgtype || 'doughnut';
    // FOR NO DATA FOUND
    this.adggraph = new Chart(pondSizectx1, temp);
  }

  /**
   * @method adggraphselection
   * @description
   * Handles the user's selection of chart type for ADG (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} adgtype - The selected chart type.
   * @example
   * this.adggraphselection('bar');
   * @returns {void}
   */
  public adggraphselection(adgtype: string) {
    if (adgtype == 'bar') {
      let ele = '<canvas id=\'adgmyChart\'></canvas>';
      $('#adgcanvasarea').html(ele);
      let pondSizectx: any = document.getElementById('adgmyChart');
      let pondSizectx1: any = pondSizectx.getContext('2d');

      if (this.adggraph) {
        this.adggraph.destroy();
      }
      this.adggraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.adggraphconfig);
      temp.type = 'bar';
      this.adggraph = new Chart(pondSizectx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('adgType', temp.type);
      }
    } else if (adgtype == 'doughnut') {
      let ele = '<canvas id=\'adgmyChart\'></canvas>';
      $('#adgcanvasarea').html(ele);
      let pondSizectx: any = document.getElementById('adgmyChart');
      let pondSizectx1: any = pondSizectx.getContext('2d');

      if (this.adggraph) {
        this.adggraph.destroy();
      }
      this.adggraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.adggraphconfig);
      temp.type = 'doughnut';
      this.adggraph = new Chart(pondSizectx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('adgType', temp.type);
      }
    }
  }

  /**
   * @method charttypesavetoDB
   * @async
   * @description
   * Saves the user's chart type preference to the database via `PreferencesService`.
   *
   * @param {string} type - The chart type to save.
   * @example
   * this.charttypesavetoDB('bar');
   * @returns {Promise<void>}
   */
  public async charttypesavetoDB(type: string) {
    try {
      this.preferences = await this.preferencesService.addPreferences(localStorage.businessID, localStorage.userID, type);
      this.pgLoad = true
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method searchBusinessABW
   * @description
   * Filters the displayed ABW list based on the `abwsearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfunabw`.
   * @example
   * this.searchBusinessABW();
   * @returns {void}
   */
  public searchBusinessABW() {
    let value = this.abwsearch.toLowerCase();
    $('.searchfunabw').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });

  }

  /**
   * @method searchBusinessCount
   * @description
   * Filters the displayed Count list based on the `countsearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfuncount`.
   * @example
   * this.searchBusinessCount();
   * @returns {void}
   */
  public searchBusinessCount() {
    let value = this.countsearch.toLowerCase();
    $('.searchfuncount').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /**
   * @method searchBusinessadg
   * @description
   * Filters the displayed ADG list based on the `adgsearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfunadg`.
   * @example
   * this.searchBusinessadg();
   * @returns {void}
   */
  public searchBusinessadg() {
    let value = this.adgsearch.toLowerCase();
    $('.searchfunadg').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }
}
